import { useState } from "react"

import { Flex, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import ButtonComponent from "../../../../../Components/Button"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import {
  CreditsAutomationConnectGuide,
  guideRequiresConfirmation
} from "../../CreditsAutomationConnectGuide"

import styles from "./AutomateCreditsModal.module.less"

const { Title, Text } = Typography

const processorImage = (serviceName) =>
  Images[`integration_${serviceName}`] || Images.integration_missing

// Several connected processors: a sidebar lets the merchant switch between them, each showing its
// own real access state - Submit stays a single account-level action (re)sending the automation
// request (CHA1-2308). Per CHA1-3389: the first tab only has Next, middle tabs have
// Previous/Next, and the last tab has Cancel/Submit - Submit stays disabled until every processor
// whose guide has a confirmation checkbox has been ticked, not just the one currently shown.
export const MultiProcessorView = ({
  processors,
  isMobile,
  onCancel,
  onSubmit,
  requestFetching
}) => {
  const { t } = useTranslation()
  const [confirmedProcessors, setConfirmedProcessors] = useState(() => new Set())

  const [activeProcessorId, setActiveProcessorId] = useState(
    () =>
      processors.find((processor) => processor.failure_reason)?.service_name ||
      processors[0].service_name
  )

  const activeIndex = processors.findIndex(
    (processor) => processor.service_name === activeProcessorId
  )
  const activeProcessor = processors[activeIndex]
  const isAlreadyAvailable = !activeProcessor.requires_request
  const isFirstTab = activeIndex === 0
  const isLastTab = activeIndex === processors.length - 1

  const handleConfirmedChange = (checked) => {
    setConfirmedProcessors((prev) => {
      const next = new Set(prev)
      if (checked) next.add(activeProcessor.service_name)
      else next.delete(activeProcessor.service_name)
      return next
    })
  }

  const canSubmit = processors
    .filter(
      (processor) => processor.requires_request && guideRequiresConfirmation(processor.service_name)
    )
    .every((processor) => confirmedProcessors.has(processor.service_name))

  const goToTab = (index) => setActiveProcessorId(processors[index].service_name)

  return (
    <Flex className={styles.wrapper} vertical={isMobile} gap={24}>
      <Flex vertical gap={16} className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Title level={5} style={{ margin: 0 }}>
            {t("Automate credits")}
          </Title>
          <Text type="secondary">
            {t("Connect your accounts to Chargeback to automate credit requests.")}
          </Text>
        </div>

        <Flex vertical gap={4}>
          {processors.map((processor) => (
            <ButtonComponent
              key={processor.service_name}
              type="text"
              data-testid={`processor-tab-${processor.service_name}`}
              className={cls(styles.processorRow, {
                [styles.processorRowActive]: processor.service_name === activeProcessorId
              })}
              onClick={() => setActiveProcessorId(processor.service_name)}
            >
              <Flex align="center" gap={8}>
                {processor.failure_reason ? (
                  <Icon
                    type="alert_triangle_error"
                    data-testid="processor-tab-alert-icon"
                    style={{ color: "#EF4243", marginRight: 4 }}
                  />
                ) : (
                  <span className={styles.checkBadge} data-testid="processor-tab-check-badge">
                    <Icon type="check" style={{ fontSize: 12 }} />
                  </span>
                )}
                <img
                  src={processorImage(processor.service_name)}
                  width={20}
                  height={20}
                  alt={processor.display_name}
                />
                <Text>{processor.display_name}</Text>
              </Flex>
            </ButtonComponent>
          ))}
        </Flex>
      </Flex>

      <Flex vertical gap={16} className={styles.content}>
        {isAlreadyAvailable ? (
          <>
            <Title level={5} style={{ margin: 0 }}>
              {t("Access already available")}
            </Title>
            <Text type="secondary">
              {t(
                "Your connected integration already gives Chargeback the transaction access required to handle system credit requests."
              )}
            </Text>
          </>
        ) : (
          <>
            <Flex align="center" gap={8}>
              <Title level={5} style={{ margin: 0 }}>
                {t("Connect _name_ account", { name: activeProcessor.display_name })}
              </Title>
              <Tag color="processing" bordered={false} style={{ margin: 0 }}>
                {t("_current_ / _total_ steps", {
                  current: activeIndex + 1,
                  total: processors.length
                })}
              </Tag>
            </Flex>

            <Text type="secondary">
              {t("Follow these steps to securely connect your _name_ account.", {
                name: activeProcessor.display_name
              })}
            </Text>

            <CreditsAutomationConnectGuide
              pp={activeProcessor.service_name}
              className={styles.guideWrapper}
              confirmed={confirmedProcessors.has(activeProcessor.service_name)}
              onConfirmedChange={handleConfirmedChange}
            />
            {activeProcessor.failure_reason && (
              <Text className={styles.warningText}>
                {activeProcessor.failure_details || t(activeProcessor.failure_reason)}
              </Text>
            )}
          </>
        )}

        <Flex justify="end" gap={8} className={cls(styles.footer, styles.footerBottom)}>
          {isLastTab ? (
            <>
              <ButtonComponent size="large" onClick={onCancel}>
                {t("Cancel")}
              </ButtonComponent>
              <ButtonComponent
                size="large"
                type="primary"
                loading={requestFetching}
                disabled={!canSubmit}
                onClick={onSubmit}
              >
                {t("Submit")}
              </ButtonComponent>
            </>
          ) : (
            <>
              {!isFirstTab && (
                <ButtonComponent size="large" onClick={() => goToTab(activeIndex - 1)}>
                  {t("Previous")}
                </ButtonComponent>
              )}
              <ButtonComponent size="large" type="primary" onClick={() => goToTab(activeIndex + 1)}>
                {t("Next")}
              </ButtonComponent>
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default MultiProcessorView
